import React, { useEffect, useState, useCallback } from "react";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import { ConversationSummary, listConversations, deleteConversation } from "../../../services/ataService";
import styles from "./AtaHistoryPanel.module.css";

interface AtaHistoryPanelProps {
    token?: string;
    activeId?: number;
    savedAt?: string; // ISO string — triggers list refresh
    onSelect: (id: number) => void;
    onNewChat: () => void;
    collapsed?: boolean;
    onToggleCollapse?: () => void;
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Date inconnue";

    const now = new Date();

    // Compare local calendar days (not rolling 24h windows) to avoid
    // classifying yesterday's late-night entries as "Aujourd'hui".
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateLocal = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((todayLocal.getTime() - dateLocal.getTime()) / 86400000);

    if (diffDays === 0) return "Aujourd'hui " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 1) return "Hier " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const AtaHistoryPanel: React.FC<AtaHistoryPanelProps> = ({ token, activeId, savedAt, onSelect, onNewChat, collapsed, onToggleCollapse }) => {
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await listConversations(token);
            setConversations(list);
        } catch {
            /* non-blocking */
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        load();
    }, [load, savedAt]);

    const handleDelete = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        try {
            await deleteConversation(id, token);
            setConversations(prev => prev.filter(c => c.id !== id));
        } catch {
            /* non-blocking */
        }
    };

    return (
        <div className={`${styles.panel} ${collapsed ? styles.panelCollapsed : ""}`}>
            <div className={styles.header}>
                {!collapsed && <span className={styles.title}>Historique</span>}
                {!collapsed && (
                    <button className={styles.newBtn} onClick={onNewChat} title="Nouvelle conversation">
                        <Icon iconName="Add" />
                        Nouveau
                    </button>
                )}
                <button className={styles.collapseBtn} onClick={onToggleCollapse} title={collapsed ? "Afficher l'historique" : "Masquer l'historique"}>
                    <Icon iconName={collapsed ? "ChevronRight" : "ChevronLeft"} />
                </button>
            </div>

            {!collapsed && (
                <>
                    <div className={styles.list}>
                        {loading ? (
                            <div className={styles.loading}>
                                <Spinner size={SpinnerSize.small} />
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className={styles.empty}>Aucune conversation sauvegardée</div>
                        ) : (
                            conversations.map(c => (
                                <div
                                    key={c.id}
                                    className={`${styles.item} ${c.id === activeId ? styles.itemActive : ""}`}
                                    onClick={() => onSelect(c.id)}
                                    title={c.title}
                                >
                                    <Icon iconName="Chat" style={{ color: "#605e5c", fontSize: 14, flexShrink: 0 }} />
                                    <div className={styles.itemBody}>
                                        <div className={styles.itemTitle}>{c.title || "Conversation sans titre"}</div>
                                        <div className={styles.itemDate}>{formatDate(c.createdAt)}</div>
                                        {c.storeNames.length > 0 && (
                                            <div className={styles.itemCategories}>
                                                {c.storeNames.slice(0, 3).join(", ")}
                                                {c.storeNames.length > 3 ? ` +${c.storeNames.length - 3}` : ""}
                                            </div>
                                        )}
                                    </div>
                                    <button className={styles.deleteBtn} onClick={e => handleDelete(e, c.id)} title="Supprimer">
                                        <Icon iconName="Delete" style={{ fontSize: 12 }} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {savedAt && (
                        <div className={styles.saveIndicator}>
                            <Icon iconName="CheckMark" style={{ fontSize: 10, marginRight: 4 }} />
                            Sauvegardé automatiquement
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AtaHistoryPanel;
